import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOddsFormatIs, ODDS_FORMAT, type OddsFormat, type OddsFormatUppercase } from '@cx-web/domain-types';
import classnames from 'classnames';

import { type Repub, REPUBS } from '@cx-modules/constants';

import Icon from '../../../../primitives/icon/index';
import { useDesktopHeaderTracking } from '../../context/DesktopHeaderTrackingContext';
import caretStyles from '../dropdownCaret.module.css';

import styles from './OddsFormatDropdown.module.css';

type OddsFormatDropdownProps = {
  oddsFormat: OddsFormat;
  oddsFormatOptions?: OddsFormatUppercase[];
  onOddsFormatChange: (format: OddsFormat) => void;
  repub?: Repub;
};

type OddsFormatOption = {
  analyticsSelection: string;
  example: string;
  format: OddsFormat;
  labelKey: string;
  testId: string;
};

const MENU_ID = 'odds-format-menu';

const OPTION_AMERICAN: OddsFormatOption = {
  analyticsSelection: 'American',
  example: '+125',
  format: ODDS_FORMAT.AMERICAN,
  labelKey: 'RESPONSIVE_ODDS_FORMAT_AMERICAN',
  testId: 'odds-format-option-american',
};
const OPTION_DECIMAL: OddsFormatOption = {
  analyticsSelection: 'Decimal',
  example: '2.25',
  format: ODDS_FORMAT.DECIMAL,
  labelKey: 'RESPONSIVE_ODDS_FORMAT_DECIMAL',
  testId: 'odds-format-option-decimal',
};
const OPTION_FRACTIONAL: OddsFormatOption = {
  analyticsSelection: 'Fraction',
  example: '5/4',
  format: ODDS_FORMAT.FRACTIONAL,
  labelKey: 'RESPONSIVE_ODDS_FORMAT_FRACTIONAL',
  testId: 'odds-format-option-fractional',
};

// Bridges SiteConfig's uppercase format tokens to the dropdown's lowercase-keyed option definitions.
const OPTION_BY_FORMAT: Record<OddsFormatUppercase, OddsFormatOption> = {
  AMERICAN: OPTION_AMERICAN,
  DECIMAL: OPTION_DECIMAL,
  FRACTIONAL: OPTION_FRACTIONAL,
};

// Prefer the explicit list from SiteConfig — it drives the dropdown menu items.
// When config provides nothing, fall back to the repub gate: OC (UK) shows
// Fractional + Decimal only; every other repub keeps the three existing formats.
const getOptions = (repub?: Repub, oddsFormatOptions?: OddsFormatUppercase[]): OddsFormatOption[] => {
  if (oddsFormatOptions?.length) {
    return oddsFormatOptions.map(format => OPTION_BY_FORMAT[format]).filter(Boolean);
  }
  return repub === REPUBS.OC.name
    ? [OPTION_FRACTIONAL, OPTION_DECIMAL]
    : [OPTION_AMERICAN, OPTION_DECIMAL, OPTION_FRACTIONAL];
};

const OddsFormatDropdown: React.FC<OddsFormatDropdownProps> = ({
  oddsFormat,
  oddsFormatOptions,
  onOddsFormatChange,
  repub,
}) => {
  const { t } = useTranslation(['label']);
  const track = useDesktopHeaderTracking();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const oddsFormatIs = getOddsFormatIs(oddsFormat);
  const isSelectedByFormat: Record<string, boolean> = {
    [ODDS_FORMAT.AMERICAN]: oddsFormatIs.American,
    [ODDS_FORMAT.DECIMAL]: oddsFormatIs.Decimal,
    [ODDS_FORMAT.FRACTIONAL]: oddsFormatIs.Fractional,
  };

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const label = t('RESPONSIVE_ODDS');

  const selectFormat = (format: OddsFormat, isSelected: boolean, analyticsSelection: string) => {
    track('oddsFormat', analyticsSelection);
    if (!isSelected) onOddsFormatChange(format);
    setIsOpen(false);
  };

  return (
    <div className={styles.container} data-open={isOpen} data-testid="odds-format" ref={containerRef}>
      <button
        aria-controls={MENU_ID}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={styles.toggle}
        data-testid="odds-format-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        type="button"
      >
        {label}
        <span aria-hidden="true" className={classnames(caretStyles.caret, isOpen && caretStyles.caretOpen)} />
      </button>

      <div className={styles.panelWrapper}>
        <div aria-label={label} className={styles.panel} id={MENU_ID} role="group">
          <ul className={styles.list}>
            {getOptions(repub, oddsFormatOptions).map(option => {
              const isSelected = isSelectedByFormat[option.format];
              return (
                <li className={styles.item} key={option.format}>
                  <button
                    aria-pressed={isSelected}
                    className={styles.option}
                    data-testid={option.testId}
                    onClick={() => selectFormat(option.format, isSelected, option.analyticsSelection)}
                    type="button"
                  >
                    {`${t(option.labelKey)} (${option.example})`}
                    {isSelected && <Icon className={styles.selectedIcon} height={16} icon="CircleTick" width={16} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OddsFormatDropdown;
